import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { API_URL } from '../config';
import AddItemToOrderPopup from './AddItemToOrderPopup';
import {
  DEFAULT_SETTINGS, LB_PER_KG, factors, enteredQty, enteredPrice, canonQty, rowAmounts,
  currencySymbol, unitLabel, hasImportCosts, trimNum, fmt, extraPerUnitByItem,
} from '../utils/orderUnits';
import './UpdateOrderStatusPopup.css';

const STATUS_BADGE = {
  S:  { bg: '#dbeafe', color: '#1d4ed8', label: 'Shipped' },
  OP: { bg: '#fef9c3', color: '#854d0e', label: 'Order Placed' },
  BO: { bg: '#fee2e2', color: '#b91c1c', label: 'Backorder' },
  D:  { bg: '#dcfce7', color: '#166534', label: 'Delivered' },
  C:  { bg: '#f3f4f6', color: '#374151', label: 'Cancelled' },
  Q:  { bg: '#f3e8ff', color: '#7e22ce', label: 'Quarantine' },
};

const ATTACH_KIND_LABELS = { invoice: 'Invoice', packing_list: 'Packing list', other: 'Other' };

// Чип оплаты в шапке заказа (цвета как в списке заказов)
const PAY_CHIP = {
  paid:    { label: 'Paid',    bg: '#dcfce7', color: '#166534' },
  partial: { label: 'Partial', bg: '#fef9c3', color: '#854d0e' },
  overdue: { label: 'Overdue', bg: '#fee2e2', color: '#b91c1c' },
  unpaid:  { label: 'Unpaid',  bg: '#f3f4f6', color: '#374151' },
};

// Columns that can be hidden via the "Columns" dropdown (applies to all orders, saved in localStorage)
const TOGGLABLE_COLS = [
  { key: 'sku',      label: 'SKU' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'est',      label: 'Est. Delivery' },
  { key: 'bo',       label: 'BO Follow-up' },
  { key: 'tracking', label: 'Tracking' },
];
const HIDDEN_COLS_KEY = 'moHiddenCols';
// По умолчанию скрыты (24.09.2026): включаются в меню Columns; BO Follow-up сама показывается,
// пока в заказе есть строка в статусе BO — иначе дату follow-up некуда вписать
const DEFAULT_HIDDEN_COLS = { bo: true, tracking: true };
const SHOW_COGS_KEY = 'mo_show_cogs';

/**
 * MaterialOrderDetail
 *
 * Editable table for all rows of an order. Supports:
 *   - Editing qty/price/status/dates/tracking
 *   - Adding new items via popup
 *   - Deleting individual rows (with confirm)
 *   - Deleting the entire order (with confirm)
 *
 * Props:
 *   orderID        — string
 *   tabKey         — tab slug
 *   materialtypeID — number|null (null for seeds)
 *   statuses       — [{ statusID, status_name }]
 *   brands         — [{ brandID, brandName }]
 *   onBack         — () => void
 */
const MaterialOrderDetail = ({
  orderID,
  tabKey,
  materialtypeID,
  statuses,
  brands,
  onBack,
}) => {
  const [rows, setRows]       = useState([]);
  const [edited, setEdited]   = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [error, setError]     = useState(null);

  // Выбранный поставщик (null = карточки поставщиков, string = товары поставщика)
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [showAddPopup,    setShowAddPopup]    = useState(false);
  const [confirmDelete,   setConfirmDelete]   = useState(null); // row index
  const [confirmDelOrder, setConfirmDelOrder] = useState(false);
  const [deleting,        setDeleting]        = useState(false);

  // Сортировка таблицы: 'item' | 'price' | 'amount' | 'est'
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // "Применить Est. Delivery ко всем позициям"
  const [applyDateAll, setApplyDateAll] = useState(false);

  // Видимость колонок (общая настройка для всех заказов, localStorage)
  const [hiddenCols, setHiddenCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HIDDEN_COLS_KEY)) || DEFAULT_HIDDEN_COLS; }
    catch { return DEFAULT_HIDDEN_COLS; }
  });
  const [showColsMenu, setShowColsMenu] = useState(false);
  const colVisible = (key) => !hiddenCols[key];
  const toggleCol = (key) => {
    setHiddenCols(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(HIDDEN_COLS_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Комментарий заказа
  const [comment, setComment]             = useState('');
  const [loadedComment, setLoadedComment] = useState('');
  const [commentMsg, setCommentMsg]       = useState('');

  // Вложения заказа (инвойсы / пекинг-листы) и оплата
  const [attachments, setAttachments] = useState([]);
  const [attachKind, setAttachKind]   = useState('invoice');
  const [uploading, setUploading]     = useState(false);
  const [attachMsg, setAttachMsg]     = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [payment, setPayment]   = useState({ payment_status: 'unpaid', payment_due_date: '', deposit_date: '', payment_date: '' });
  const [paySaving, setPaySaving] = useState(false);
  const [payMsg, setPayMsg]       = useState('');
  // Данные оплаты не загрузились — Save заблокирован, иначе перезатрёт реальную запись дефолтами
  const [payLoadError, setPayLoadError] = useState(false);

  // ─── Extra costs + настройки поставщика в заказе (все вкладки) ──────────────
  // Map: { [supplierID]: { packaging, freight_handling, miscellaneous, customs_compliance,
  //                        currency, fx_rate, weight_unit, is_import } } — строка order_extra_costs
  const [extraCostsMap, setExtraCostsMap] = useState({});
  const [extraSaving,   setExtraSaving]   = useState(false);
  const [extraSaveMsg,  setExtraSaveMsg]  = useState('');

  // Панель настроек: курс редактируется черновиком, применяется по blur / Enter
  // Колонки для сверки с COGS: цена и доп. расходы в USD за lb | pcs по каждой строке, Amount ($) у EUR.
  // Общая настройка для всех заказов (localStorage), как видимость колонок.
  const [showCogs, setShowCogs] = useState(() => {
    try { return localStorage.getItem(SHOW_COGS_KEY) === '1'; } catch { return false; }
  });
  const toggleShowCogs = (on) => {
    setShowCogs(on);
    try { localStorage.setItem(SHOW_COGS_KEY, on ? '1' : '0'); } catch { /* приватный режим — настройка на сеанс */ }
  };
  const [rateDraft,      setRateDraft]      = useState('');
  const [pendingEur,     setPendingEur]     = useState(false);   // выбран EUR, курс ещё не введён
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg,    setSettingsMsg]    = useState('');

  const isSeeds         = tabKey === 'seeds';
  const isFertilizer    = tabKey === 'fertilizer-orders';
  const isMasterBags    = tabKey === 'master-bags';
  const isSmallBags     = tabKey === 'small-bags';
  const isMiniTools     = tabKey === 'mini-tools';
  const isDesiccant     = tabKey === 'desiccant-bags';
  const isReadyProducts = tabKey === 'ready-products';
  const isBagType       = isMasterBags || isSmallBags || isMiniTools || isDesiccant;
  const isWeightTab     = isSeeds || isFertilizer;   // вес в lb | kg, цена за единицу веса

  const extraCostsUrl  = isFertilizer ? '/api/fert-order-extra-costs' : '/api/material-orders/order-extra-costs';
  const extraCostsType = isMasterBags ? 'master-bags' : isSmallBags ? 'small-bags' : isMiniTools ? 'mini-tools'
    : isDesiccant ? 'desiccant-bags' : isReadyProducts ? 'ready-products' : 'seeds';

  // Настройки поставщика в заказе; без строки — USD / lb, не импорт
  function settingsOf(supplierID) {
    return { ...DEFAULT_SETTINGS, ...(extraCostsMap[supplierID] || {}) };
  }

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  // silent — перечитать строки после сохранения: без спиннера на весь блок и без сброса сообщений
  const fetchDetail = useCallback(async ({ silent = false } = {}) => {
    if (!orderID) return;
    if (!silent) { setLoading(true); setSaveMsg(''); }
    setError(null);
    setEdited({});
    try {
      let data;
      if (isFertilizer) {
        const res = await api.get('/api/fert-orders-detail', {
          params: { orderID },
        });
        data = res.data;
      } else if (isSeeds) {
        const res = await api.get('/api/material-orders/seeds-detail', {
          params: { orderID },
        });
        data = res.data;
      } else if (isReadyProducts) {
        const res = await api.get('/api/material-orders/ready-products-detail', {
          params: { orderID },
        });
        data = res.data;
      } else {
        const res = await api.get('/api/orders_by_orderid', {
          params: { orderID, materialtypeID },
        });
        data = res.data;
      }
      setRows(data || []);

      // Fetch order comment
      try {
        const cRes = await api.get('/api/material-orders/comments', {
          params: { orderType: tabKey, orderIDs: orderID },
        });
        const c = (cRes.data || {})[orderID] || '';
        setComment(c);
        setLoadedComment(c);
      } catch (e) {
        console.error('Error fetching order comment:', e);
      }

      // Fetch attachments + payment info.
      // Сначала сбрасываем прошлое состояние: при смене заказа без анмаунта
      // здесь иначе остаются файлы/оплата предыдущего заказа.
      setAttachments([]);
      setPreviewFile(null);
      setPayment({ payment_status: 'unpaid', payment_due_date: '', deposit_date: '', payment_date: '' });
      setPayLoadError(false);
      try {
        const [aRes, pRes] = await Promise.all([
          api.get('/api/material-orders/attachments', { params: { orderType: tabKey, orderID } }),
          api.get('/api/material-orders/payment',     { params: { orderType: tabKey, orderID } }),
        ]);
        setAttachments(aRes.data || []);
        const p = pRes.data;
        setPayment({
          payment_status:   p?.payment_status   || 'unpaid',
          payment_due_date: p?.payment_due_date || '',
          deposit_date:     p?.deposit_date     || '',
          payment_date:     p?.payment_date     || '',
        });
      } catch (e) {
        console.error('Error fetching attachments/payment:', e);
        setPayLoadError(true);
      }

      // Доп. расходы и настройки поставщика (валюта, курс, единица, Import order) — все вкладки
      if (isSeeds || isFertilizer || isBagType || isReadyProducts) {
        try {
          const orderType = isMasterBags    ? 'master-bags'
            : isSmallBags      ? 'small-bags'
            : isMiniTools      ? 'mini-tools'
            : isDesiccant      ? 'desiccant-bags'
            : isReadyProducts  ? 'ready-products'
            : 'seeds';
          const url = isFertilizer ? '/api/fert-order-extra-costs' : '/api/material-orders/order-extra-costs';
          const ecRes = await api.get(url, { params: { orderID, orderType } });
          // Суммы — строками для контролируемых полей, настройки — как есть
          const rawMap = ecRes.data || {};
          const normalised = {};
          for (const [supID, ec] of Object.entries(rawMap)) {
            normalised[supID] = {
              packaging:          ec.packaging          != null ? String(ec.packaging)          : '',
              freight_handling:   ec.freight_handling   != null ? String(ec.freight_handling)   : '',
              miscellaneous:      ec.miscellaneous      != null ? String(ec.miscellaneous)      : '',
              customs_compliance: ec.customs_compliance != null ? String(ec.customs_compliance) : '',
              currency:    ec.currency || 'USD',
              fx_rate:     ec.fx_rate != null ? Number(ec.fx_rate) : null,
              weight_unit: ec.weight_unit || 'lb',
              is_import:   ec.is_import ? 1 : 0,
            };
          }
          setExtraCostsMap(normalised);
        } catch (e) {
          console.error('Error fetching extra costs:', e);
        }
      }
    } catch (err) {
      console.error('Error fetching order detail:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderID, tabKey, isSeeds, isFertilizer, isReadyProducts, isBagType, isMasterBags, isSmallBags, isMiniTools, isDesiccant, materialtypeID]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // ─── Edit helpers ──────────────────────────────────────────────────────────
  const getRow = (i) => ({ ...rows[i], ...(edited[i] || {}) });

  const handleChange = (i, field, value) => {
    setEdited(prev => ({ ...prev, [i]: { ...(prev[i] || {}), [field]: value } }));
    setSaveMsg('');
  };

  const isDirty = Object.keys(edited).length > 0;

  // Применить статус ко всем видимым строкам (либо к поставщику, либо ко всем).
  // Строки, уже сохранённые как D (получены), не трогаем: смена D → другой статус
  // откатывает сток на сервере, для этого есть построчный селект.
  const applyStatusToVisible = (statusID) => {
    setEdited(prev => {
      const next = { ...prev };
      activeEntries.forEach(({ i }) => {
        if (rows[i].statusID === 'D') return;
        next[i] = { ...(next[i] || {}), statusID };
      });
      return next;
    });
    setSaveMsg('');
  };

  // Изменение Est. Delivery: при включённой галочке применяем ко всем видимым не-BO строкам
  const handleEstDateChange = (i, value) => {
    if (applyDateAll) {
      setEdited(prev => {
        const next = { ...prev };
        activeEntries.forEach(({ i: idx }) => {
          const statusID = (next[idx]?.statusID) ?? rows[idx].statusID;
          // BO не имеет Est. Delivery, D уже получен — дату не трогаем
          if (statusID !== 'BO' && statusID !== 'D') next[idx] = { ...(next[idx] || {}), est_del_date: value };
        });
        // Строка, которую редактируют напрямую, меняется всегда (даже если она D)
        next[i] = { ...(next[i] || {}), est_del_date: value };
        return next;
      });
      setSaveMsg('');
    } else {
      handleChange(i, 'est_del_date', value);
    }
  };

  // ─── Sorting ───────────────────────────────────────────────────────────────
  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortIndicator = (key) =>
    sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // ─── Order comment ─────────────────────────────────────────────────────────
  const handleSaveComment = async () => {
    if (comment === loadedComment) return;
    try {
      await api.put('/api/material-orders/comment', {
        orderID, orderType: tabKey, comment,
      });
      setLoadedComment(comment);
      setCommentMsg('Saved');
      setTimeout(() => setCommentMsg(''), 2000);
    } catch (err) {
      console.error('Error saving comment:', err);
      setCommentMsg('Error');
    }
  };

  // ─── Attachments (invoices / packing lists) ────────────────────────────────
  const handleUploadAttachments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploading(true);
    setAttachMsg('');
    try {
      const fd = new FormData();
      fd.append('orderID', orderID);
      fd.append('orderType', tabKey);
      fd.append('kind', attachKind);
      files.forEach(f => fd.append('files', f));
      const res = await api.post('/api/material-orders/attachments', fd);
      setAttachments(res.data || []);
      setAttachMsg('Saved');
      setTimeout(() => setAttachMsg(''), 2000);
    } catch (err) {
      console.error('Error uploading attachments:', err);
      setAttachMsg(err.response?.data?.error || 'Error');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.original_name}"?`)) return;
    try {
      await api.delete(`/api/material-orders/attachments/${att.id}`);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch (err) {
      console.error('Error deleting attachment:', err);
      alert('Failed to delete file');
    }
  };

  // ─── Payment ───────────────────────────────────────────────────────────────
  const handleSavePayment = async () => {
    setPaySaving(true);
    setPayMsg('');
    try {
      await api.put('/api/material-orders/payment', {
        orderID, orderType: tabKey, ...payment,
      });
      setPayMsg('Saved');
      setTimeout(() => setPayMsg(''), 2000);
    } catch (err) {
      console.error('Error saving payment:', err);
      setPayMsg('Error');
    } finally {
      setPaySaving(false);
    }
  };

  // ─── Save edits ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const dirtyRows = Object.keys(edited).map(idx => ({
        ...rows[parseInt(idx)],
        ...edited[parseInt(idx)],
        prevStatusID: rows[parseInt(idx)].statusID,
      }));

      const supportsInventory = materialtypeID === 1 || materialtypeID === 2 || materialtypeID === 3 || materialtypeID === 6;

      // Цена и вес уходят как введённые (единица и валюта поставщика) — канон lb / USD считает сервер
      const enteredOf = (r) => {
        const s = settingsOf(r.supplierID);
        return {
          price_entered: enteredPrice(r, s, isWeightTab),
          ...(isWeightTab ? { qty_entered: enteredQty(r, s, true), lab_testing: r.lab_testing ?? 0 } : {}),
          duties: r.duties ?? 0,
        };
      };

      if (isFertilizer) {
        await api.put(
          '/api/fert-orders-update',
          {
            rows: dirtyRows.map(r => ({
              id:                r.id,
              rawMaterialID:     r.rawMaterialID,
              orderID:           r.orderID,
              supplierID:        r.supplierID,
              year:              r.year,
              month_num:         r.month_num,
              ...enteredOf(r),
              est_del_date:      r.statusID === 'BO' ? null : (r.est_del_date || null),
              bo_follow_up_date: r.statusID === 'BO' ? (r.bo_follow_up_date || null) : null,
              statusID:          r.statusID,
              prevStatusID:      r.prevStatusID,
              tracking:          r.tracking || null,
            })),
          }
        );
      } else if (isSeeds) {
        await api.put('/api/material-orders/seeds-update', {
          rows: dirtyRows.map(r => ({
            id:                r.id,
            seedsID:           r.seedsID,
            orderID:           r.orderID,
            supplierID:        r.supplierID,
            year:              r.year,
            month_num:         r.month_num,
            ...enteredOf(r),
            est_del_date:      r.statusID === 'BO' ? null : (r.est_del_date || null),
            bo_follow_up_date: r.statusID === 'BO' ? (r.bo_follow_up_date || null) : null,
            statusID:          r.statusID,
          })),
        });
      } else if (isReadyProducts) {
        await api.put('/api/material-orders/ready-products-update', {
          rows: dirtyRows.map(r => ({
            id:                r.id,
            sku:               r.sku,
            orderID:           r.orderID,
            year:              r.year,
            month_num:         r.month_num,
            qty:               r.qty,
            ...enteredOf(r),
            est_del_date:      r.statusID === 'BO' ? null : (r.est_del_date || null),
            bo_follow_up_date: r.statusID === 'BO' ? (r.bo_follow_up_date || null) : null,
            statusID:          r.statusID,
            prevStatusID:      r.prevStatusID,
            supplierID:        r.supplierID,
            tracking:          r.tracking || null,
          })),
        });
      } else if (supportsInventory) {
        // Small Bags (1) и Master Bags (2) — используем update_order_rows,
        // который обновляет инвентарь и пишет историю при смене статуса на/с 'D'
        await api.put(
          '/api/update_order_rows',
          {
            rows: dirtyRows.map(r => ({
              id:                r.id,
              sku:               r.sku,
              year:              r.year,
              month_num:         r.month_num,
              orderID:           r.orderID,
              supplierID:        r.supplierID,
              materialtypeID:    r.materialtypeID,
              statusID:          r.statusID,
              qty:               r.qty,
              prevStatusID:      r.prevStatusID,
              ...enteredOf(r),
              est_del_date:      r.statusID === 'BO' ? null : (r.est_del_date || null),
              bo_follow_up_date: r.statusID === 'BO' ? (r.bo_follow_up_date || null) : null,
              tracking:          r.tracking || null,
            })),
          }
        );
      } else {
        await api.put('/api/material-orders/update', {
          rows: dirtyRows.map(r => ({
            id:                r.id,
            sku:               r.sku,
            year:              r.year,
            month_num:         r.month_num,
            orderID:           r.orderID,
            supplierID:        r.supplierID,
            materialtypeID:    r.materialtypeID,
            qty:               r.qty,
            ...enteredOf(r),
            est_del_date:      r.statusID === 'BO' ? null : (r.est_del_date || null),
            bo_follow_up_date: r.statusID === 'BO' ? (r.bo_follow_up_date || null) : null,
            statusID:          r.statusID,
          })),
        });
      }

      setRows(prev => prev.map((r, i) => edited[i] ? { ...r, ...edited[i] } : r));
      setEdited({});
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 3000);
      // Канон (lb / USD) и cost-таблицы пересчитал сервер — перечитываем строки без спиннера
      await fetchDetail({ silent: true });
    } catch (err) {
      console.error('Error saving order:', err);
      setSaveMsg('Error saving');
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete single row ─────────────────────────────────────────────────────
  const handleDeleteRow = async (index) => {
    setDeleting(true);
    try {
      const row = rows[index];
      if (isFertilizer) {
        await api.delete('/api/fert-orders-delete-item', { data: { id: row.id } });
      } else if (isReadyProducts) {
        await api.delete('/api/material-orders/ready-products-delete-item', { data: { id: row.id } });
      } else {
        await api.delete('/api/material-orders/delete-item', {
          data: {
            isSeeds,
            id:             row.id,
            sku:            row.sku,
            seedsID:        row.seedsID,
            year:           row.year,
            month_num:      row.month_num,
            materialtypeID,
          },
        });
      }
      setRows(prev => prev.filter((_, i) => i !== index));
      // Shift edited keys above the deleted index
      setEdited(prev => {
        const next = {};
        Object.keys(prev).forEach(k => {
          const ki = parseInt(k);
          if (ki < index)  next[ki]     = prev[k];
          else if (ki > index) next[ki - 1] = prev[k];
        });
        return next;
      });
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting row:', err);
      alert('Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  // ─── Delete order (or just one supplier's rows from it) ───────────────────
  const handleDeleteOrder = async () => {
    setDeleting(true);
    // When a specific supplier is selected, scope deletion to that supplier only
    const supplierIDToDelete = selectedSupplier
      ? (supplierGroups.find(g => g.supplier === selectedSupplier)?.entries[0]?.row?.supplierID ?? null)
      : null;

    try {
      if (isFertilizer) {
        await api.delete('/api/fert-orders-delete-order', {
          data: {
            orderID,
            ...(supplierIDToDelete != null ? { supplierID: supplierIDToDelete } : {}),
          },
        });
      } else if (isReadyProducts) {
        await api.delete('/api/material-orders/ready-products-delete-order', {
          data: {
            orderID,
            ...(supplierIDToDelete != null ? { supplierID: supplierIDToDelete } : {}),
          },
        });
      } else {
        await api.delete('/api/material-orders/delete-order', {
          data: {
            isSeeds,
            orderID,
            materialtypeID,
            ...(supplierIDToDelete != null ? { supplierID: supplierIDToDelete } : {}),
          },
        });
      }

      if (supplierIDToDelete != null) {
        // Remove deleted rows from local state and go back to supplier cards
        // (or fully away if no rows remain)
        const remaining = rows.filter(r => r.supplierID !== supplierIDToDelete);
        if (remaining.length > 0) {
          setRows(remaining);
          setEdited({});
          setSelectedSupplier(null);
          setConfirmDelOrder(false);
        } else {
          onBack();
        }
      } else {
        onBack();
      }
    } catch (err) {
      console.error('Error deleting order:', err);
      alert('Failed to delete order');
      setDeleting(false);
      setConfirmDelOrder(false);
    }
  };

  // ─── Save extra costs for a specific supplier ─────────────────────────────
  const handleSaveExtraCosts = async (supplierID) => {
    setExtraSaving(true);
    setExtraSaveMsg('');
    const ec = extraCostsMap[supplierID] || {};
    try {
      // Частичный upsert: настройки поставщика (валюта, курс, единица, Import order) не трогаем
      await api.post(extraCostsUrl, {
        orderID,
        orderType:          extraCostsType,
        supplierID:         parseInt(supplierID) || 0,
        packaging:          parseFloat(ec.packaging          || 0),
        freight_handling:   parseFloat(ec.freight_handling   || 0),
        miscellaneous:      parseFloat(ec.miscellaneous      || 0),
        customs_compliance: parseFloat(ec.customs_compliance || 0),
      });
      setExtraSaveMsg('Saved');
      setTimeout(() => setExtraSaveMsg(''), 3000);
    } catch (err) {
      console.error('Error saving extra costs:', err);
      setExtraSaveMsg('Error');
    } finally {
      setExtraSaving(false);
    }
  };

  // Helper to update extraCostsMap for a supplier field
  const handleExtraCostChange = (supplierID, field, value) => {
    setExtraCostsMap(prev => ({
      ...prev,
      [supplierID]: { ...(prev[supplierID] || { packaging: '', freight_handling: '', miscellaneous: '', customs_compliance: '' }), [field]: value },
    }));
  };

  // ─── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (val) => {
    if (!val) return '';
    if (val instanceof Date) return val.toISOString().split('T')[0];
    return String(val).slice(0, 10);
  };

  const useLbQty = isWeightTab;
  const hasExtraCosts = isSeeds || isFertilizer || isBagType || isReadyProducts;

  // Количество в lb | pcs и суммы строки (в валюте поставщика и в USD) — из введённых значений,
  // чтобы итоги менялись по мере правки; у старых строк введённое восстанавливается из канона
  const qtyOf   = (r) => canonQty(r, settingsOf(r.supplierID), isWeightTab);
  const amounts = (r) => rowAmounts(r, settingsOf(r.supplierID), isWeightTab);

  const totalQty    = rows.reduce((sum, r, i) => sum + qtyOf(getRow(i)), 0);
  const totalAmount = rows.reduce((sum, r, i) => sum + amounts(getRow(i)).usd, 0);

  const firstRow = rows[0];
  const isPeriodOrder = false; // period- orders now display their key directly
  const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  // Группировка строк по поставщику (сохраняем оригинальный индекс для edited-стейта)
  const supplierGroups = (() => {
    const map = {};
    const order = [];
    rows.forEach((row, i) => {
      const key = row.supplierName || '—';
      if (!map[key]) { map[key] = []; order.push(key); }
      map[key].push({ row, i });
    });
    return order.map(key => ({ supplier: key, entries: map[key] }));
  })();
  const hasMultipleSuppliers = supplierGroups.length > 1;

  // Строки для текущего вида:
  //  - если один поставщик — все строки
  //  - если выбран поставщик — только его строки
  const activeEntries = hasMultipleSuppliers && selectedSupplier
    ? (supplierGroups.find(g => g.supplier === selectedSupplier)?.entries || [])
    : (hasMultipleSuppliers ? [] : (supplierGroups[0]?.entries || []));

  // Для отображения поставщиков в заголовке
  const supplierLabel = supplierGroups.map(g => g.supplier).filter(s => s !== '—').join(', ') || null;

  // ─── Настройки поставщика в заказе: валюта, курс, единица, Import order ────
  // Лежат в той же строке order_extra_costs, что и доп. расходы; таблица показывает строки
  // одного поставщика, поэтому и настройки берём его
  const currentSupplierID = hasMultipleSuppliers
    ? (supplierGroups.find(g => g.supplier === selectedSupplier)?.entries[0]?.row?.supplierID ?? null)
    : (rows[0]?.supplierID ?? null);
  const settings = settingsOf(currentSupplierID);
  const isEUR    = settings.currency === 'EUR';
  const isKg     = isWeightTab && settings.weight_unit === 'kg';
  const isImport = !!settings.is_import;
  const cur      = currencySymbol(settings.currency);
  const uLabel   = unitLabel(settings, isWeightTab);
  const { kUnit, kFx } = factors(settings, isWeightTab);
  const settingsBusy = settingsSaving || isDirty;

  // Поле курса показывает сохранённый курс; при смене поставщика или курса — пересинхронизация
  useEffect(() => {
    setRateDraft(settings.fx_rate ? String(settings.fx_rate) : '');
    setPendingEur(false);
    setSettingsMsg('');
  }, [currentSupplierID, settings.fx_rate, settings.currency]);

  // Частичный POST настроек, затем перечитать заказ: канон строк и cost-таблицы пересчитал сервер
  const saveSettings = async (fields) => {
    setSettingsSaving(true);
    setSettingsMsg('');
    try {
      await api.post(extraCostsUrl, { orderID, orderType: extraCostsType, supplierID: currentSupplierID, ...fields });
      setPendingEur(false);
      await fetchDetail({ silent: true });
    } catch (err) {
      console.error('Error saving order settings:', err);
      setSettingsMsg(err.response?.data?.error || 'Error');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleCurrencyChange = async (currency) => {
    if (currency !== 'EUR') return saveSettings({ currency });
    let rate = Number(settings.fx_rate) > 0 ? Number(settings.fx_rate) : (Number(rateDraft) > 0 ? Number(rateDraft) : null);
    if (!rate) {
      // Подстановка курса ECB на сегодня; правится руками, применённый курс хранится в заказе
      try { const { data } = await api.get('/api/material-orders/fx-rate'); rate = data.rate; } catch (_) { /* ниже — ручной ввод */ }
    }
    if (!rate) { setPendingEur(true); setRateDraft(''); setSettingsMsg('Enter the EUR→USD rate to apply'); return; }
    setRateDraft(String(rate));
    await saveSettings({ currency: 'EUR', fx_rate: rate });
  };

  const handleRateCommit = async () => {
    const rate = parseFloat(rateDraft);
    if (!(rate > 0)) { setSettingsMsg('Rate must be a positive number'); return; }
    if (!pendingEur && rate === Number(settings.fx_rate)) return;
    await saveSettings({ currency: 'EUR', fx_rate: rate });
  };

  const handleImportToggle = async (checked) => {
    if (!checked && hasImportCosts(settings, rows) &&
        !window.confirm('Customs & Compliance, Duties and Lab Testing will be hidden and excluded from COGS. The amounts stay saved. Continue?')) return;
    await saveSettings({ is_import: checked ? 1 : 0 });
  };

  // Значение поля ввода: пока редактируется — как набрано, иначе из строки (введённое или восстановленное из канона)
  const inputVal = (i, field, derived, digits) =>
    (edited[i]?.[field] !== undefined ? edited[i][field] : trimNum(derived, digits));

  // Сумма по строке в USD (сортировка, карточки поставщиков, общие итоги)
  const rowAmount = (r) => amounts(r).usd;

  // Колонка BO Follow-up: по настройке или пока среди видимых строк есть Backorder
  const showBo = colVisible('bo') || activeEntries.some(({ i }) => getRow(i).statusID === 'BO');

  // Вид таблицы позиций (один поставщик или выбранный из нескольких) — в отличие от обзора карточек и пустого заказа
  const tableView = rows.length > 0 && (!hasMultipleSuppliers || !!selectedSupplier);

  // Отсортированные строки для отображения
  const displayEntries = (() => {
    if (!sortKey) return activeEntries;
    const val = ({ i }) => {
      const r = getRow(i);
      switch (sortKey) {
        case 'item':   return (r.itemName || '').toLowerCase();
        case 'price':  return enteredPrice(r, settingsOf(r.supplierID), isWeightTab);
        case 'amount': return rowAmount(r);
        case 'est':    return formatDate(r.est_del_date); // '' сортируется первым
        default:       return 0;
      }
    };
    return [...activeEntries].sort((a, b) => {
      const va = val(a), vb = val(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  })();

  // Даты создания/закрытия заказа (lead time)
  const createdDates = rows.map(r => r.created_at).filter(Boolean).map(d => String(d).slice(0, 10)).sort();
  const orderCreated = createdDates[0] || null;
  const allDelivered = rows.length > 0 && rows.every((r, i) => getRow(i).statusID === 'D');
  const deliveredDates = rows.map(r => r.delivered_date).filter(Boolean).map(d => String(d).slice(0, 10)).sort();
  const orderClosed = allDelivered && deliveredDates.length > 0 ? deliveredDates[deliveredDates.length - 1] : null;
  const leadDays = orderCreated && orderClosed
    ? Math.max(0, Math.round((new Date(orderClosed) - new Date(orderCreated)) / 86400000))
    : null;

  // ─── Ключевые цифры шапки: по видимым строкам (выбранный поставщик или весь заказ) ───
  const n = v => Number(v) || 0;
  const visEntries  = activeEntries.length ? activeEntries : rows.map((row, i) => ({ row, i }));
  const goodsNative = visEntries.reduce((s, { i }) => s + amounts(getRow(i)).native, 0);
  const goodsUsd    = visEntries.reduce((s, { i }) => s + amounts(getRow(i)).usd, 0);
  const qtyCanon    = visEntries.reduce((s, { i }) => s + qtyOf(getRow(i)), 0);
  const qtyEntered  = isKg ? visEntries.reduce((s, { i }) => s + enteredQty(getRow(i), settings, true), 0) : null;
  // Доп. расходы поставщика: Packaging + Freight + Misc (+ Customs у импортного) — из черновика карты,
  // поэтому цифра в шапке меняется по мере ввода в карточке расходов
  const extrasOf = (sid) => {
    const e = extraCostsMap[sid];
    return e ? n(e.packaging) + n(e.freight_handling) + n(e.miscellaneous) + (e.is_import ? n(e.customs_compliance) : 0) : 0;
  };
  const extrasUsd = (currentSupplierID != null ? [currentSupplierID] : [...new Set(rows.map(r => r.supplierID))])
    .reduce((s, sid) => s + extrasOf(sid), 0);
  const importUsd = visEntries.reduce((s, { i }) => {
    const r = getRow(i);
    return settingsOf(r.supplierID).is_import ? s + n(r.duties) + n(r.lab_testing) : s;
  }, 0);
  const landedUsd    = goodsUsd + extrasUsd + importUsd;
  const extraPerUnit = qtyCanon > 0 ? (extrasUsd + importUsd) / qtyCanon : 0;
  // Доп. расходы за lb | pcs по каждой позиции — как их разнесёт сервер: общие по количеству поставщика,
  // Duties и Lab Testing — только своей позиции. Цифра в шапке — среднее по заказу.
  const canonUnit  = useLbQty ? 'lb' : 'pcs';
  const itemKeyOf  = (r) => (isFertilizer ? r.rawMaterialID : isSeeds ? r.seedsID : r.sku);
  const extraByItem = extraPerUnitByItem(rows.map((_, i) => {
    const r = getRow(i);
    return { supplierID: r.supplierID, itemID: itemKeyOf(r), qty: qtyOf(r), duties: r.duties, lab_testing: isWeightTab ? r.lab_testing : 0 };
  }), extraCostsMap);
  const showCanonPrice = showCogs && (isEUR || isKg);     // цена отличается от введённой
  const showExtraCol   = showCogs && hasExtraCosts;
  const statusChips  = [...new Set(visEntries.map(({ i }) => getRow(i).statusID).filter(Boolean))];
  const payOverdue   = payment.payment_status !== 'paid' && payment.payment_due_date
    && payment.payment_due_date < new Date().toLocaleDateString('en-CA');
  const payChip      = payOverdue ? PAY_CHIP.overdue : (PAY_CHIP[payment.payment_status] || PAY_CHIP.unpaid);

  // ─── Render guards ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="mo-loading">
        <i className="fas fa-spinner fa-spin"></i> Loading order...
      </div>
    );
  }

  if (error) return <div className="mo-empty">{error}</div>;

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Шапка: заголовок и действия · ключевые цифры · настройки поставщика в заказе ── */}
      <div className="mo-card mo-head">
        <div className="mo-head-top">
          <button className="mo-back-btn" onClick={hasMultipleSuppliers && selectedSupplier ? () => setSelectedSupplier(null) : onBack}>
            <i className="fas fa-arrow-left"></i> Back
          </button>

          <div className="mo-head-title">
            <div className="mo-detail-title">
              {isPeriodOrder
                ? <span style={{ color: '#9ba3b0', fontStyle: 'italic', fontSize: '0.9em' }}>No PO# (period order)</span>
                : orderID}
            </div>
            <div className="mo-detail-subtitle">
              {firstRow ? `${MONTH_NAMES[(firstRow.month_num || 1) - 1]} ${firstRow.year}` : ''}
              {supplierLabel ? ` · ${supplierLabel}` : ''}
              {orderCreated ? ` · Created ${orderCreated}` : ''}
              {orderClosed ? ` · Delivered ${orderClosed}${leadDays != null ? ` (lead time ${leadDays} d)` : ''}` : ''}
            </div>
          </div>

          <div className="mo-head-actions">
            {saveMsg === 'Saved' && (
              <div className="mo-save-success">
                <i className="fas fa-check-circle"></i> Saved
              </div>
            )}
            {saveMsg === 'Error saving' && (
              <div style={{ color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="fas fa-exclamation-circle"></i> Error saving
              </div>
            )}
            <button
              className="mo-save-btn"
              onClick={handleSave}
              disabled={!isDirty || saving}
            >
              {saving
                ? <><i className="fas fa-spinner fa-spin"></i> Saving…</>
                : <><i className="fas fa-save"></i> Save changes</>}
            </button>
            <button className="mo-add-item-btn" onClick={() => setShowAddPopup(true)}>
              <i className="fas fa-plus"></i> Add item
            </button>
            {!confirmDelOrder ? (
              <button className="mo-delete-order-btn" onClick={() => setConfirmDelOrder(true)}>
                <i className="fas fa-trash"></i>{' '}
                {selectedSupplier ? `Delete ${selectedSupplier}` : 'Delete order'}
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#dc2626', whiteSpace: 'nowrap' }}>
                  {selectedSupplier
                    ? `Delete all items from ${selectedSupplier}?`
                    : 'Delete entire order?'}
                </span>
                <button
                  style={{ padding: '5px 12px', background: '#dc2626', color: '#fff',
                           border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                           whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={handleDeleteOrder}
                  disabled={deleting}
                >
                  {deleting ? '…' : 'Yes, delete'}
                </button>
                <button
                  style={{ padding: '5px 12px', background: 'none', border: '1px solid #dde1e7',
                           borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
                  onClick={() => setConfirmDelOrder(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Ключевые цифры: товар в валюте поставщика и USD, доп. расходы, импорт, полная стоимость */}
        {rows.length > 0 && (
          <div className="mo-head-stats">
            <div className="mo-stat">
              <span className="mo-stat-label">Goods</span>
              <span className="mo-stat-value">
                {isEUR ? `€${fmt(goodsNative)}` : `$${fmt(goodsUsd)}`}
                {isEUR && <small> ≈ ${fmt(goodsUsd)}</small>}
              </span>
            </div>
            <div className="mo-stat" title="Packaging + Freight & Handling + Miscellaneous (+ Customs & Compliance for import orders)">
              <span className="mo-stat-label">Extras</span>
              <span className="mo-stat-value">${fmt(extrasUsd)}</span>
            </div>
            {isImport && (
              <div className="mo-stat" title="Duties + Lab Testing of the rows">
                <span className="mo-stat-label">Import costs</span>
                <span className="mo-stat-value">${fmt(importUsd)}</span>
              </div>
            )}
            <div className="mo-stat mo-stat--accent" title="Goods + extras + import costs, USD">
              <span className="mo-stat-label">Landed cost</span>
              <span className="mo-stat-value">${fmt(landedUsd)}</span>
            </div>
            <div className="mo-stat" title={`Order average: (extras + import costs) ÷ quantity. Duties and Lab Testing go to their own item only. Tick "Show $/${canonUnit} for COGS" to see each row.`}>
              <span className="mo-stat-label">Avg extra per {canonUnit}</span>
              <span className="mo-stat-value">${extraPerUnit.toFixed(4)}</span>
            </div>
            <div className="mo-stat">
              <span className="mo-stat-label">Items</span>
              <span className="mo-stat-value">
                {visEntries.length} · {fmt(qtyCanon, useLbQty ? 2 : 0)} {useLbQty ? 'lb' : 'pcs'}
                {isKg && <small> ({fmt(qtyEntered, 2)} kg)</small>}
              </span>
            </div>
            <div className="mo-head-chips">
              {statusChips.map(s => {
                const badge = STATUS_BADGE[s] || { bg: '#f3f4f6', color: '#374151' };
                return (
                  <span key={s} className="mo-chip" style={{ background: badge.bg, color: badge.color }} title={badge.label || s}>{s}</span>
                );
              })}
              <span className="mo-chip" style={{ background: payChip.bg, color: payChip.color }} title="Payment">{payChip.label}</span>
            </div>
          </div>
        )}

        {/* Валюта, курс, единица и Import order поставщика в заказе (order_extra_costs) */}
        {currentSupplierID != null && (
          <div className="mo-head-settings">
            <label>
              Currency
              <select
                className="mo-cell-select"
                value={pendingEur ? 'EUR' : settings.currency}
                disabled={settingsBusy}
                onChange={e => handleCurrencyChange(e.target.value)}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
            {(isEUR || pendingEur) && (
              <label>
                Rate EUR→USD
                <input
                  type="number"
                  className="mo-cell-input"
                  style={{ width: 92 }}
                  min="0"
                  step="0.0001"
                  placeholder="1.0000"
                  value={rateDraft}
                  disabled={settingsBusy}
                  title="ECB rate for today is prefilled; edit if the invoice used another rate"
                  onChange={e => { setRateDraft(e.target.value); setSettingsMsg(''); }}
                  onBlur={handleRateCommit}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                />
              </label>
            )}
            {isWeightTab && (
              <label>
                Unit
                <select
                  className="mo-cell-select"
                  value={settings.weight_unit}
                  disabled={settingsBusy}
                  onChange={e => saveSettings({ weight_unit: e.target.value })}
                >
                  <option value="lb">lb</option>
                  <option value="kg">kg</option>
                </select>
              </label>
            )}
            <label style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={isImport} disabled={settingsBusy} onChange={e => handleImportToggle(e.target.checked)} />
              Import order
            </label>
            <label style={{ cursor: 'pointer' }} title={`Price and extra costs in USD per ${canonUnit} for every row: the figures that go to COGS`}>
              <input type="checkbox" checked={showCogs} onChange={e => toggleShowCogs(e.target.checked)} />
              Show $/{canonUnit} for COGS
            </label>
            {settingsSaving && (
              <span className="mo-muted"><i className="fas fa-spinner fa-spin"></i> Applying…</span>
            )}
            {isDirty && !settingsSaving && (
              <span className="mo-muted">Save or discard row changes before changing these</span>
            )}
            {settingsMsg && (
              <span className="mo-msg-err"><i className="fas fa-exclamation-circle"></i> {settingsMsg}</span>
            )}
          </div>
        )}
      </div>

      {/* ── Тело: сетка 2×2 — строка над таблицей (крошка поставщика, массовый статус) только в левой
             колонке, ниже слева таблица, справа сайдбар: его верх совпадает с верхом таблицы ── */}
      <div className="mo-body">
      {tableView && (
        <div className="mo-main-top">
          {/* Breadcrumb поставщика */}
          {hasMultipleSuppliers && selectedSupplier && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 0 2px', fontSize: '0.83rem', color: '#94a3b8' }}>
              <span
                style={{ cursor: 'pointer', color: '#64748b' }}
                onClick={() => { setSelectedSupplier(null); setConfirmDelete(null); }}
              >
                All suppliers
              </span>
              <span>/</span>
              <span style={{ color: '#334155', fontWeight: 600 }}>
                <i className="fas fa-truck" style={{ marginRight: 5, fontSize: '0.78rem' }}></i>
                {selectedSupplier}
              </span>
            </div>
          )}

          {/* Bulk status bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0 4px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.83rem', color: '#64748b' }}>Set all to:</span>
            {statuses.map(s => {
              const badge = STATUS_BADGE[s.statusID];
              return (
                <button
                  key={s.statusID}
                  className="uos-bulk-btn"
                  style={badge ? { background: badge.bg, color: badge.color } : {}}
                  onClick={() => applyStatusToVisible(s.statusID)}
                >
                  {s.statusID}
                </button>
              );
            })}

            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.83rem',
                            color: '#64748b', marginLeft: 14, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={applyDateAll}
                onChange={e => setApplyDateAll(e.target.checked)}
              />
              Apply Est. Delivery to all
            </label>

            <div style={{ marginLeft: 'auto', position: 'relative' }}>
              <button className="uos-bulk-btn" onClick={() => setShowColsMenu(v => !v)}>
                <i className="fas fa-columns" style={{ marginRight: 5 }}></i>Columns
              </button>
              {showColsMenu && (
                <div style={{
                  position: 'absolute', right: 0, top: '115%', zIndex: 30,
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.12)', padding: '8px 12px', minWidth: 170,
                }}>
                  {TOGGLABLE_COLS.map(c => (
                    <label key={c.key} style={{ display: 'flex', gap: 8, alignItems: 'center',
                                                fontSize: 13, padding: '4px 0', cursor: 'pointer',
                                                whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={colVisible(c.key)} onChange={() => toggleCol(c.key)} />
                      {c.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="mo-main">
      {/* ── Вид: карточки поставщиков ───────────────────────────────────────── */}
      {rows.length === 0 ? (
        <div className="mo-empty">
          No items in this order.{' '}
          <button
            style={{ color: '#5B965D', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}
            onClick={() => setShowAddPopup(true)}
          >
            Add one?
          </button>
        </div>
      ) : hasMultipleSuppliers && !selectedSupplier ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            {supplierGroups.map(({ supplier, entries }) => {
              const grpQty      = entries.reduce((s, { i }) => s + qtyOf(getRow(i)), 0);
              const grpAmount   = entries.reduce((s, { i }) => s + rowAmount(getRow(i)), 0);
              const grpStatuses = [...new Set(entries.map(({ i }) => getRow(i).statusID).filter(Boolean))];
              const hasDirty    = entries.some(({ i }) => edited[i]);

              return (
                <div
                  key={supplier}
                  onClick={() => setSelectedSupplier(supplier)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '14px 18px', background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 10,
                    cursor: 'pointer', transition: 'box-shadow 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <i className="fas fa-truck" style={{ color: '#64748b', fontSize: '0.9rem' }}></i>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {supplier}
                      {hasDirty && (
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} title="Unsaved changes"></span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                      {entries.length} item{entries.length !== 1 ? 's' : ''}
                      {' · '}
                      {Number(grpQty).toLocaleString('en-US', { maximumFractionDigits: 2 })} {useLbQty ? 'lb' : 'pcs'}
                      {' · '}
                      <strong style={{ color: '#437045' }}>
                        ${grpAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {grpStatuses.map(s => {
                      const badge = STATUS_BADGE[s] || { bg: '#f3f4f6', color: '#374151' };
                      return (
                        <span key={s} style={{
                          background: badge.bg, color: badge.color,
                          padding: '2px 8px', borderRadius: 12,
                          fontSize: '0.75rem', fontWeight: 600,
                        }}>{s}</span>
                      );
                    })}
                  </div>

                  <i className="fas fa-chevron-right" style={{ color: '#cbd5e1', fontSize: '0.85rem', flexShrink: 0 }}></i>
                </div>
              );
            })}
          </div>

          {/* Общий итог */}
          <div className="mo-totals-bar" style={{ marginTop: 12 }}>
            <span className="mo-totals-item">
              <i className="fas fa-layer-group"></i>
              {rows.length} item{rows.length !== 1 ? 's' : ''}
            </span>
            <span className="mo-totals-sep">·</span>
            <span className="mo-totals-item">
              <i className="fas fa-cubes"></i>
              {Number(totalQty).toLocaleString('en-US', { maximumFractionDigits: 2 })} {useLbQty ? 'lb' : 'pcs'} total
            </span>
            <span className="mo-totals-sep">·</span>
            <span className="mo-totals-item mo-totals-amount">
              <i className="fas fa-dollar-sign"></i>
              ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total
            </span>
          </div>
        </>
      ) : (
        <>
          {/* Items table (крошка поставщика и массовый статус — строкой выше, в mo-main-top) */}
          <div className="mo-detail-table-wrapper">
            <table className="mo-detail-table">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => handleSort('item')}>
                    Item{sortIndicator('item')}
                  </th>
                  {!isSeeds && !isFertilizer && colVisible('sku') && <th>SKU</th>}
                  {!hasMultipleSuppliers && colVisible('supplier') && <th>Supplier</th>}
                  <th style={{ minWidth: 110 }}>Qty ({uLabel})</th>
                  {isKg && <th style={{ minWidth: 80 }}>Qty (lb)</th>}
                  <th style={{ minWidth: 110, cursor: 'pointer' }} onClick={() => handleSort('price')}>
                    Price/{uLabel} ({cur}){sortIndicator('price')}
                  </th>
                  <th style={{ minWidth: 90, cursor: 'pointer' }} onClick={() => handleSort('amount')}>
                    Amount, {cur}{sortIndicator('amount')}
                  </th>
                  {showCanonPrice && <th style={{ minWidth: 90 }}>Price ($/{canonUnit})</th>}
                  {isEUR && showCogs && <th style={{ minWidth: 90 }}>Amount ($)</th>}
                  {isImport && <th style={{ minWidth: 90 }}>Duties ($)</th>}
                  {isImport && isWeightTab && <th style={{ minWidth: 90 }}>Lab Testing ($)</th>}
                  {showExtraCol && <th style={{ minWidth: 90 }}>Extra ($/{canonUnit})</th>}
                  <th>Status</th>
                  {colVisible('est') && (
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('est')}>
                      Est. Delivery{sortIndicator('est')}
                    </th>
                  )}
                  {showBo && <th>BO Follow-up</th>}
                  {colVisible('tracking') && <th>Tracking</th>}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {displayEntries.map(({ row: baseRow, i }) => {
                  const row       = getRow(i);
                  const isBO      = row.statusID === 'BO';
                  const qtyLocked = !isSeeds && !isFertilizer && (materialtypeID === 1 || materialtypeID === 2 || materialtypeID === 3 || materialtypeID === 6)
                    && (row.statusID === 'D' || rows[i].statusID === 'D');

                  return (
                    <tr key={isFertilizer ? `fert-${row.rawMaterialID}-${i}` : isSeeds ? `${row.seedsID}-${i}` : `${row.sku}-${i}`}>
                      <td>{row.itemName || '—'}</td>

                      {!isSeeds && !isFertilizer && colVisible('sku') && (
                        <td style={{ color: '#5a6270', fontSize: 12 }}>{row.sku}</td>
                      )}

                      {!hasMultipleSuppliers && colVisible('supplier') && (
                        <td style={{ color: '#5a6270', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {row.supplierName || '—'}
                        </td>
                      )}

                      <td>
                        <input
                          type="number"
                          className="mo-cell-input"
                          value={isWeightTab ? inputVal(i, 'qty_entered', enteredQty(row, settings, true), 3) : (row.qty ?? '')}
                          min="0"
                          step={isWeightTab ? '0.01' : '1'}
                          disabled={qtyLocked}
                          title={qtyLocked ? 'Qty cannot be edited on delivered orders' : ''}
                          style={qtyLocked ? { opacity: 0.45, cursor: 'not-allowed', background: '#f1f5f9' } : {}}
                          onChange={e => handleChange(i, isWeightTab ? 'qty_entered' : 'qty', e.target.value)}
                        />
                      </td>

                      {isKg && (
                        <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>
                          {fmt(enteredQty(row, settings, true) * LB_PER_KG, 2)}
                        </td>
                      )}

                      <td>
                        <input
                          type="number"
                          className="mo-cell-input"
                          value={inputVal(i, 'price_entered', enteredPrice(row, settings, isWeightTab), 4)}
                          min="0"
                          step="0.0001"
                          onChange={e => handleChange(i, 'price_entered', e.target.value)}
                        />
                      </td>

                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#334155' }}>
                        {fmt(amounts(row).native)}
                      </td>

                      {showCanonPrice && (
                        <td style={{ whiteSpace: 'nowrap', color: '#64748b' }} title={`Price in USD per ${canonUnit}: what goes to COGS`}>
                          {fmt(enteredPrice(row, settings, isWeightTab) * kFx / kUnit, 4)}
                        </td>
                      )}
                      {isEUR && showCogs && (
                        <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>{fmt(amounts(row).usd)}</td>
                      )}

                      {isImport && (
                        <td>
                          <input
                            type="number"
                            className="mo-cell-input"
                            min="0"
                            step="0.01"
                            value={inputVal(i, 'duties', row.duties, 2)}
                            onChange={e => handleChange(i, 'duties', e.target.value)}
                          />
                        </td>
                      )}
                      {isImport && isWeightTab && (
                        <td>
                          <input
                            type="number"
                            className="mo-cell-input"
                            min="0"
                            step="0.01"
                            value={inputVal(i, 'lab_testing', row.lab_testing, 2)}
                            onChange={e => handleChange(i, 'lab_testing', e.target.value)}
                          />
                        </td>
                      )}
                      {showExtraCol && (() => {
                        const extra = extraByItem.get(itemKeyOf(row)) || 0;
                        const price = enteredPrice(row, settings, isWeightTab) * kFx / kUnit;
                        return (
                          <td
                            style={{ whiteSpace: 'nowrap', color: '#64748b' }}
                            title={`Extra costs of this item per ${canonUnit}: shared order costs by quantity${isImport ? ' plus its own Duties and Lab Testing' : ''}. Price + extra = $${fmt(price + extra, 4)} per ${canonUnit}`}
                          >
                            {fmt(extra, 4)}
                          </td>
                        );
                      })()}

                      <td>
                        <select
                          className="mo-cell-select"
                          value={row.statusID || ''}
                          onChange={e => handleChange(i, 'statusID', e.target.value)}
                        >
                          {statuses.map(s => (
                            <option key={s.statusID} value={s.statusID}>
                              {s.statusID} — {s.status_name}
                            </option>
                          ))}
                        </select>
                      </td>

                      {colVisible('est') && (
                        <td>
                          {isBO ? (
                            <input type="text" className="mo-cell-input" disabled value="" placeholder="—"
                              style={{ opacity: 0.35, background: '#f1f5f9' }} />
                          ) : (
                            <input
                              type="date"
                              className="mo-cell-input"
                              value={formatDate(row.est_del_date)}
                              title={applyDateAll ? 'Will be applied to all items' : ''}
                              onChange={e => handleEstDateChange(i, e.target.value)}
                            />
                          )}
                        </td>
                      )}

                      {showBo && (
                        <td>
                          {!isBO ? (
                            <input type="text" className="mo-cell-input" disabled value="" placeholder="—"
                              style={{ opacity: 0.35, background: '#f1f5f9' }} />
                          ) : (
                            <input
                              type="date"
                              className="mo-cell-input"
                              value={formatDate(row.bo_follow_up_date)}
                              onChange={e => handleChange(i, 'bo_follow_up_date', e.target.value)}
                            />
                          )}
                        </td>
                      )}

                      {colVisible('tracking') && (
                        <td>
                          <input
                            type="text"
                            className="mo-cell-input"
                            value={row.tracking || ''}
                            style={{ minWidth: 120 }}
                            onChange={e => handleChange(i, 'tracking', e.target.value)}
                          />
                        </td>
                      )}

                      <td style={{ textAlign: 'center', minWidth: 70 }}>
                        {confirmDelete === i ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              style={{ padding: '3px 8px', background: '#dc2626', color: '#fff',
                                       border: 'none', borderRadius: 5, fontSize: 12, cursor: 'pointer' }}
                              onClick={() => handleDeleteRow(i)}
                              disabled={deleting}
                            >
                              {deleting ? '…' : 'Yes'}
                            </button>
                            <button
                              style={{ padding: '3px 8px', background: 'none', border: '1px solid #dde1e7',
                                       borderRadius: 5, fontSize: 12, cursor: 'pointer' }}
                              onClick={() => setConfirmDelete(null)}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            className="mo-delete-row-btn"
                            title="Delete this item"
                            onClick={() => setConfirmDelete(i)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Итог по поставщику / заказу */}
          {activeEntries.length > 0 && (() => {
            const visQty    = activeEntries.reduce((s, { i }) => s + qtyOf(getRow(i)), 0);
            const visQtyKg  = isKg ? activeEntries.reduce((s, { i }) => s + enteredQty(getRow(i), settings, true), 0) : null;
            const visNative = activeEntries.reduce((s, { i }) => s + amounts(getRow(i)).native, 0);
            const visAmount = activeEntries.reduce((s, { i }) => s + amounts(getRow(i)).usd, 0);
            return (
              <div className="mo-totals-bar" style={{ marginTop: 12 }}>
                <span className="mo-totals-item">
                  <i className="fas fa-layer-group"></i>
                  {activeEntries.length} item{activeEntries.length !== 1 ? 's' : ''}
                </span>
                <span className="mo-totals-sep">·</span>
                <span className="mo-totals-item">
                  <i className="fas fa-cubes"></i>
                  {Number(visQty).toLocaleString('en-US', { maximumFractionDigits: 2 })} {useLbQty ? 'lb' : 'pcs'} total
                  {isKg && ` (${fmt(visQtyKg, 2)} kg)`}
                </span>
                <span className="mo-totals-sep">·</span>
                <span className="mo-totals-item mo-totals-amount">
                  <i className="fas fa-dollar-sign"></i>
                  {isEUR
                    ? `€${fmt(visNative)} total (≈ $${fmt(visAmount)})`
                    : `$${fmt(visAmount)} total`}
                </span>
              </div>
            );
          })()}
        </>
      )}
      </div>{/* /mo-main */}

      {/* ── Сайдбар: доп. расходы поставщика · оплата · документы и комментарий ── */}
      <aside className="mo-side">
      {hasExtraCosts && currentSupplierID != null && (() => {
        const ec = extraCostsMap[currentSupplierID] || { packaging: '', freight_handling: '', miscellaneous: '', customs_compliance: '' };
        // Customs & Compliance — только у импортных заказов (иначе поле скрыто и в расчёт не идёт)
        const fields = [
          { key: 'packaging',        label: 'Packaging' },
          { key: 'freight_handling', label: 'Freight & Handling' },
          ...(isImport ? [{ key: 'customs_compliance', label: 'Customs & Compliance' }] : []),
          { key: 'miscellaneous',    label: 'Miscellaneous' },
        ];
        return (
          <div className="mo-card">
            <div className="mo-card-title">
              <i className="fas fa-truck-loading"></i> Additional costs
              {hasMultipleSuppliers && selectedSupplier && (
                <span className="mo-card-title-sub">{selectedSupplier}</span>
              )}
            </div>
            {fields.map(({ key, label }) => (
              <label key={key} className="mo-field-row">
                <span>{label} ($)</span>
                <input
                  type="number"
                  className="mo-cell-input"
                  min="0"
                  step="0.01"
                  value={ec[key] ?? ''}
                  onChange={e => handleExtraCostChange(currentSupplierID, key, e.target.value)}
                />
              </label>
            ))}
            <div className="mo-card-foot">
              <span className="mo-card-total">Total extra <b>${fmt(extrasOf(currentSupplierID))}</b></span>
              <button
                className="mo-btn-primary"
                onClick={() => handleSaveExtraCosts(currentSupplierID)}
                disabled={extraSaving}
              >
                {extraSaving ? 'Saving…' : 'Save costs'}
              </button>
              {extraSaveMsg === 'Saved' && (
                <span className="mo-msg-ok"><i className="fas fa-check-circle"></i> Saved</span>
              )}
              {extraSaveMsg === 'Error' && (
                <span className="mo-msg-err"><i className="fas fa-exclamation-circle"></i> Error</span>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Оплата ──────────────────────────────────────────────────────────── */}
      <div className="mo-card">
        <div className="mo-card-title">
          <i className="fas fa-dollar-sign"></i> Payment
          <span className="mo-chip" style={{ background: payChip.bg, color: payChip.color }}>{payChip.label}</span>
        </div>
        <label className="mo-field-row">
          <span>Status</span>
          <select
            className="mo-cell-select"
            value={payment.payment_status}
            onChange={e => setPayment(p => ({ ...p, payment_status: e.target.value }))}
          >
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partially paid (deposit)</option>
            <option value="paid">Paid</option>
          </select>
        </label>
        {[
          { key: 'payment_due_date', label: 'Due date' },
          { key: 'deposit_date',     label: 'Deposit paid' },
          { key: 'payment_date',     label: 'Paid in full' },
        ].map(({ key, label }) => (
          <div key={key} className="mo-field-row">
            <span>{label}</span>
            {/* Крестик обязателен: пикер даты в iOS Safari не умеет очищать значение */}
            <span className="mo-field-input">
              <input
                type="date"
                className="mo-cell-input"
                value={payment[key] || ''}
                onChange={e => setPayment(p => ({ ...p, [key]: e.target.value }))}
              />
              {payment[key] && (
                <button type="button" title="Clear" className="mo-clear-btn" onClick={() => setPayment(p => ({ ...p, [key]: '' }))}>×</button>
              )}
            </span>
          </div>
        ))}
        <div className="mo-card-foot">
          <button
            className="mo-btn-primary"
            onClick={handleSavePayment}
            disabled={paySaving || payLoadError}
          >
            {paySaving ? 'Saving…' : 'Save payment'}
          </button>
          {payLoadError && (
            <span className="mo-msg-err"><i className="fas fa-exclamation-circle"></i> Failed to load payment data — reload the page</span>
          )}
          {payMsg === 'Saved' && (
            <span className="mo-msg-ok"><i className="fas fa-check-circle"></i> Saved</span>
          )}
          {payMsg === 'Error' && (
            <span className="mo-msg-err"><i className="fas fa-exclamation-circle"></i> Error</span>
          )}
        </div>
      </div>

      {/* ── Документы (инвойсы / пекинг-листы) и комментарий ─────────────────── */}
      <div className="mo-card">
        <div className="mo-card-title">
          <i className="fas fa-paperclip"></i> Documents
          {attachMsg === 'Saved' && (
            <span className="mo-msg-ok"><i className="fas fa-check-circle"></i> Saved</span>
          )}
          {attachMsg && attachMsg !== 'Saved' && (
            <span className="mo-msg-err"><i className="fas fa-exclamation-circle"></i> {attachMsg}</span>
          )}
          <span className="mo-card-title-right">
            <select
              className="mo-cell-select"
              value={attachKind}
              onChange={e => setAttachKind(e.target.value)}
              title="Document type for the next upload"
            >
              <option value="invoice">Invoice</option>
              <option value="packing_list">Packing list</option>
              <option value="other">Other</option>
            </select>
            <label className={`mo-btn-primary${uploading ? ' is-busy' : ''}`}>
              <i className="fas fa-upload"></i>
              {uploading ? 'Uploading…' : 'Upload'}
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                disabled={uploading}
                style={{ display: 'none' }}
                onChange={e => { handleUploadAttachments(e.target.files); e.target.value = ''; }}
              />
            </label>
          </span>
        </div>
        {attachments.length === 0 ? (
          <div className="mo-muted">No documents yet — upload an invoice or packing list.</div>
        ) : (
          <div className="mo-doc-list">
            {attachments.map(att => (
              <div key={att.id} className="mo-doc-row">
                <i className={`fas ${/\.pdf$/i.test(att.filename) ? 'fa-file-pdf' : 'fa-file-image'}`}></i>
                <button className="mo-doc-name" onClick={() => setPreviewFile(att)} title="Preview">
                  {att.original_name}
                </button>
                <span className="mo-doc-meta">
                  {ATTACH_KIND_LABELS[att.kind] || 'Other'}
                  {' · '}
                  {att.size_bytes >= 1024 * 1024
                    ? `${(att.size_bytes / 1024 / 1024).toFixed(1)} MB`
                    : `${Math.max(1, Math.round(att.size_bytes / 1024))} KB`}
                  {' · '}
                  {att.uploaded_at}
                </span>
                <a
                  className="mo-doc-dl"
                  href={`${API_URL}/uploads/order-docs/${att.filename}`}
                  download={att.original_name}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open / download"
                >
                  <i className="fas fa-download"></i>
                </a>
                <button className="mo-doc-del" onClick={() => handleDeleteAttachment(att)} title="Delete">
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mo-card-subtitle">
          <i className="fas fa-comment-alt"></i> Comment
          {commentMsg === 'Saved' && (
            <span className="mo-msg-ok"><i className="fas fa-check-circle"></i> Saved</span>
          )}
          {commentMsg === 'Error' && (
            <span className="mo-msg-err"><i className="fas fa-exclamation-circle"></i> Error</span>
          )}
        </div>
        <textarea
          rows={2}
          className="mo-comment"
          value={comment}
          placeholder="Add a comment for this order…"
          onChange={e => { setComment(e.target.value); setCommentMsg(''); }}
          onBlur={handleSaveComment}
        />
      </div>
      </aside>
      </div>{/* /mo-body */}

      {/* Attachment preview */}
      {previewFile && (() => {
        const url   = `${API_URL}/uploads/order-docs/${previewFile.filename}`;
        const isPdf = /\.pdf$/i.test(previewFile.filename);
        return (
          <div
            onClick={() => setPreviewFile(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(15,23,42,0.6)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 10, maxWidth: '90vw', maxHeight: '90vh',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {previewFile.original_name}
                </span>
                <a
                  href={url}
                  download={previewFile.original_name}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open full document in a new tab"
                  style={{ marginLeft: 'auto', color: '#64748b', whiteSpace: 'nowrap', fontSize: 13 }}
                >
                  <i className="fas fa-external-link-alt" style={{ marginRight: 4 }}></i>
                  Open
                </a>
                <button
                  onClick={() => setPreviewFile(null)}
                  style={{ background: 'none', border: 'none', fontSize: 20, color: '#64748b', cursor: 'pointer', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
              {isPdf ? (
                <iframe src={url} title={previewFile.original_name} style={{ width: '85vw', height: '80vh', border: 'none' }} />
              ) : (
                <img src={url} alt={previewFile.original_name} style={{ maxWidth: '85vw', maxHeight: '80vh', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        );
      })()}

      {/* Add item popup */}
      {showAddPopup && (
        <AddItemToOrderPopup
          orderID={orderID}
          tabKey={tabKey}
          materialtypeID={materialtypeID}
          year={firstRow?.year || new Date().getFullYear()}
          month_num={firstRow?.month_num || new Date().getMonth() + 1}
          statuses={statuses}
          brands={brands}
          settings={settingsOf(currentSupplierID ?? rows[0]?.supplierID ?? null)}
          defaults={{
            supplierID:   currentSupplierID,
            supplierName: rows.find(r => r.supplierID === currentSupplierID)?.supplierName,
            // последняя дата доставки среди строк заказа (выбранного поставщика)
            estDelDate:   visEntries.map(({ i }) => formatDate(getRow(i).est_del_date)).filter(Boolean).sort().pop() || '',
          }}
          onClose={() => setShowAddPopup(false)}
          onSaved={fetchDetail}
        />
      )}
    </div>
  );
};

export default MaterialOrderDetail;
